import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../constants/types";
import { ERROR_MESSAGES } from "../../constants/enums";
import { api } from "../../api/api";
import { IChatMessage, IHistoryContentPart, IHistoryItem } from "./aiChat.types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Converts the raw API history item into the app's IChatMessage format.
 * Handles both:
 *  - user messages: context.content is a plain string
 *  - assistant messages: context.content is an array of { text, type } parts
 */
const mapHistoryItem = (item: IHistoryItem): IChatMessage => {
  const ctx = item.context;
  let content = "";

  if (typeof ctx.content === "string") {
    content = ctx.content;
  } else if (Array.isArray(ctx.content)) {
    content = (ctx.content as IHistoryContentPart[])
      .filter((p) => p.type === "output_text")
      .map((p) => p.text)
      .join("\n");
  }

  return {
    id: ctx.id,
    role: ctx.role === "assistant" ? "ai" : ctx.role,
    content,
    timestamp: item.insert_ts
      ? new Date(item.insert_ts).getTime()
      : Date.now(),
  };
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

/**
 * Fetches a page of chat history.
 * Returns messages in chronological order (oldest first).
 * The API returns newest-first, so we reverse the array.
 */
export const fetchChatHistory = createAsyncThunk<
  { messages: IChatMessage[]; hasMore: boolean; page: number },
  { pageNo: number; itemPerPage?: number },
  { rejectValue: APIError }
>(
  "aiChat/fetchChatHistory",
  async ({ pageNo, itemPerPage = 10 }: { pageNo: number; itemPerPage?: number }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/a2a/chat?pageNo=${pageNo}&itemPerPage=${itemPerPage}`,
      );
      const items: IHistoryItem[] = Array.isArray(res.data)
        ? res.data
        : (res.data?.data ?? []);

      const messages = items.map(mapHistoryItem).reverse();

      return {
        messages,
        hasMore: items.length >= itemPerPage,
        page: pageNo,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

/**
 * Sends a new user message to the AI.
 * On success, the streaming response arrives via socket events, so this
 * thunk only initiates the request. The response is NOT stored here.
 */
export const sendChatMessage = createAsyncThunk<
  void,
  { message: string; pendingId: string },
  { rejectValue: APIError & { pendingId?: string } }
>(
  "aiChat/sendChatMessage",
  async ({ message, pendingId }: { message: string; pendingId: string }, { rejectWithValue }) => {
    try {
      await api.post("/a2a/chat", { message });
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
        pendingId,
      });
    }
  },
);
